import type { LetterboxGeometry } from './types';

/** 元フレームを size×size に収める letterbox の幾何(114 グレー埋め、中央寄せ)。丸めは Math.round。 */
export function letterboxGeometry(sw: number, sh: number, size: number): LetterboxGeometry {
  const r = Math.min(size / sw, size / sh);
  const nw = Math.round(sw * r);
  const nh = Math.round(sh * r);
  return { size, r, dx: Math.round((size - nw) / 2), dy: Math.round((size - nh) / 2), sw, sh };
}

/** RGBA(HWC, 0..255)を RGB の CHW(0..1)に変換する。 */
export function rgbaToChw(rgba: Uint8ClampedArray, size: number, out?: Float32Array): Float32Array {
  const n = size * size;
  const f = out ?? new Float32Array(3 * n);
  for (let i = 0, p = 0; i < n; i++, p += 4) {
    f[i] = (rgba[p] as number) / 255;
    f[n + i] = (rgba[p + 1] as number) / 255;
    f[2 * n + i] = (rgba[p + 2] as number) / 255;
  }
  return f;
}

/** canvas を 1 枚持ち回して、映像フレームを letterbox → CHW テンソルにする。 */
export class Preprocessor {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly buf: Float32Array;

  constructor(readonly size: number) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = size;
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('2d canvas unavailable');
    this.ctx = ctx;
    this.buf = new Float32Array(3 * size * size);
  }

  /**
   * 映像フレームを letterbox して CHW テンソルにする。
   * 返る `data` は内部バッファの使い回しで、次の toTensor 呼び出しで上書きされる(保持するならコピーすること)。
   */
  toTensor(source: CanvasImageSource, sw: number, sh: number): { data: Float32Array; geom: LetterboxGeometry } {
    if (sw <= 0 || sh <= 0) throw new Error('invalid source size');
    const geom = letterboxGeometry(sw, sh, this.size);
    const nw = Math.round(sw * geom.r);
    const nh = Math.round(sh * geom.r);
    this.ctx.fillStyle = 'rgb(114,114,114)';
    this.ctx.fillRect(0, 0, this.size, this.size);
    this.ctx.drawImage(source, 0, 0, sw, sh, geom.dx, geom.dy, nw, nh);
    const { data } = this.ctx.getImageData(0, 0, this.size, this.size);
    return { data: rgbaToChw(data, this.size, this.buf), geom };
  }
}
